import { notify as defaultNotify } from "./toast";

export function getApiErrorMessage(error, fallback = "요청 처리 중 오류가 발생했습니다.") {
    const data = error?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (error?.message) return error.message;
    return fallback;
}

/**
 * 서버 메시지가 <b>사용자에게 보여줄 만한 것</b>인지.
 *
 * <p>{@link getApiErrorMessage} 는 마지막 두 단계에서 <b>영문 기술 문자열</b>을 흘린다:
 * 본문 없는 응답({@code ResponseEntity.status(404).build()})이면 axios 의
 * {@code "Request failed with status code 404"}, 네트워크 끊김이면 {@code "Network Error"},
 * 타임아웃이면 {@code "timeout of 30000ms exceeded"}, 본문에 message 없이 error 만 있으면
 * {@code "FORBIDDEN"} 같은 코드가 그대로 나온다.
 *
 * <p>그래서 {@code errorMsg: (msg) => msg || "채널 생성 실패"} 로 바꾸면 <b>더 나빠진다</b> —
 * msg 가 비는 일이 거의 없어서 폴백이 안 걸리고, 사용자는 한국어 안내 대신 영문 기술 문구를 본다.
 * 판단 기준은 "비었나" 가 아니라 "사람이 읽을 한국어인가" 여야 한다.
 */
function isUserFacing(msg) {
    if (typeof msg !== "string" || !msg.trim()) return false;
    // 한글이 한 글자도 없으면 서버가 준 사람 대상 문구가 아니다 (영문 코드·axios 기술 메시지).
    return /[가-힣]/.test(msg);
}

/**
 * {@code runApiAction} 의 {@code errorMsg} 로 넘길 함수를 만든다.
 *
 * <p>서버가 한국어로 <b>이유</b>를 말해주면 그걸 그대로 보여주고, 아니면 화면이 정한 문구를 쓴다.
 * 문자열을 그냥 넘기면 서버 문구가 <b>항상</b> 버려진다 — 실제로 채널 생성이 그랬다.
 * 서버는 "이미 존재하는 채널 이름입니다: xxx" 라고 답했는데 화면엔 "채널 생성 실패" 만 떴고,
 * 사용자도 신고를 받은 사람도 무엇을 고쳐야 하는지 알 수 없었다.
 *
 * <pre>{@code errorMsg: serverMsgOr("채널 생성 실패")}</pre>
 */
export function serverMsgOr(fallback) {
    return (msg) => (isUserFacing(msg) ? msg : fallback);
}

export function assertApiResponse(response, fallback = "서버 응답이 올바르지 않습니다.") {
    if (response && typeof response !== "object") return response;

    if (response && !("status" in response) && !("data" in response)) {
        if (response?.success === false || response?.ok === false) {
            throw new Error(response?.message || response?.error || fallback);
        }
        return response;
    }

    const status = response?.status;
    if (status && (status < 200 || status >= 300)) {
        throw new Error(fallback);
    }

    const data = response?.data;
    if (data == null || data?.success === false || data?.ok === false) {
        throw new Error(data?.message || data?.error || fallback);
    }

    return data;
}

/**
 * @param {object} options
 * @param {() => Promise<any>} options.request
 * @param {string | ((serverMsg: string) => string)} [options.errorMsg]
 *   - string: 서버 메시지 무시하고 이 문자열을 toast에 표시
 *   - function: 서버 메시지를 받아 가공 후 표시
 *   - 미전달: 서버 에러 메시지 그대로 표시
 * @param {string | ((data: any) => string | { message: string, type: string })} [options.successMsg]
 *   - 미전달: 성공 toast 없음
 * @param {(data: any) => boolean | string} [options.validate]
 * @param {(loading: boolean) => void} [options.setLoading]
 * @param {(data: any) => void | Promise<void>} [options.onSuccess]
 * @param {(error: Error, message: string) => void | Promise<void>} [options.onError]
 * @param {() => void} [options.closeOnSuccess]
 * @param {boolean} [options.silent=false] - true면 toast 완전 비활성화
 * @param {boolean} [options.throwOnError=false]
 */
export async function runApiAction({
    request,
    errorMsg,
    successMsg,
    validate,
    setLoading,
    onSuccess,
    onError,
    closeOnSuccess,
    silent = false,
    throwOnError = false,
}) {
    const doNotify = silent ? () => {} : defaultNotify;

    try {
        setLoading?.(true);
        const data = await request();

        const validationResult = validate?.(data);
        if (typeof validationResult === "string") throw new Error(validationResult);
        if (validationResult === false) throw new Error("유효성 검사에 실패했습니다.");

        if (successMsg !== undefined) {
            const result = typeof successMsg === "function" ? successMsg(data) : successMsg;
            if (typeof result === "string") doNotify(result, "success");
            else if (result?.message) doNotify(result.message, result.type ?? "success");
        }

        await onSuccess?.(data);
        closeOnSuccess?.();
        return { ok: true, data };

    } catch (error) {
        const message = typeof errorMsg === "function"
            ? errorMsg(error.message)
            : (errorMsg ?? error.message ?? "요청 처리 중 오류가 발생했습니다.");
        doNotify(message, "error");
        await onError?.(error, message);
        if (throwOnError) throw error;
        return { ok: false, error, message };

    } finally {
        setLoading?.(false);
    }
}
